import { computed, inject, Injectable, Injector } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import { UrlSegment } from '@app/shared/types/enums/url-segment.enum';
import { filter, Observable, take, tap } from 'rxjs';
import { ActiveCompanyService } from '../services/active-company.service';
import { AuthService } from '../services/auth.service';
import { CompaniesDataService } from '../services/companies-data.service';
import { LocalStorageService } from '../services/local-storage.service';

/**
 * DirectUserCompanyGuard - Allows company admin routes (groups, users, industries, cooperations)
 * only when the company in the URL is a direct-user company.
 *
 * On refresh the signed-in user and company list are still loading, so this guard waits for
 * that data instead of treating a missing company as "not direct" and bouncing to projects.
 *
 * Scope: Global (`providedIn: 'root'`).
 *
 * Usage: `canActivate` on groups, users, industries, and cooperations routes.
 */
@Injectable({
  providedIn: 'root'
})
export class DirectUserCompanyGuard {
  private readonly activeCompanyService = inject(ActiveCompanyService);
  private readonly companiesDataService = inject(CompaniesDataService);
  private readonly authService = inject(AuthService);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly router = inject(Router);
  private readonly injector = inject(Injector);

  public canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    const companyId = this.getCompanyIdFromRoute(route) ?? this.activeCompanyService.activeCompany()?.id ?? null;

    const decision = computed((): boolean | UrlTree | undefined => {
      const user = this.authService.currentUser();
      const hasTokens = !!this.localStorageService.tokens()?.accessToken;

      if (!user) {
        return hasTokens ? undefined : this.router.createUrlTree([UrlSegment.SIGN_IN]);
      }

      const companyCollection = this.companiesDataService.getUserCompanyCollection(user.id)();
      if (!companyCollection?.id) {
        return undefined;
      }

      return this.resolveAccess(companyId);
    });

    return toObservable(decision, { injector: this.injector }).pipe(
      tap(() => {
        const user = this.authService.currentUser();
        if (user?.id) {
          this.companiesDataService.loadUserCompanies(user.id);
        }
      }),
      filter((value): value is boolean | UrlTree => value !== undefined),
      take(1)
    );
  }

  private resolveAccess(companyId: string | null): boolean | UrlTree {
    if (!companyId) {
      return this.router.createUrlTree([UrlSegment.REPORTS]);
    }

    const company = this.companiesDataService.getById(companyId)();
    if (company?.directUserCompany === true) {
      return true;
    }

    return this.router.createUrlTree([UrlSegment.COMPANIES, companyId, UrlSegment.PROJECTS_LIST, UrlSegment.ACTIVE]);
  }

  private getCompanyIdFromRoute(route: ActivatedRouteSnapshot): string | null {
    let current: ActivatedRouteSnapshot | null = route;
    while (current) {
      const id = current.paramMap.get('company-id');
      if (id) return id;
      current = current.parent;
    }
    return null;
  }
}
